// src/components/more/More.js
import React from 'react';
import { Camera, Video, ChevronRight, LogOut, User, School, PencilRuler, MessageCircle, Newspaper, Activity, Gift } from 'lucide-react';
import { useAtom } from 'jotai';
import { alertModal, appConstants, auth_token, tab, survey_id, childList, activeChildDetail, parentProfile, last_reload, last_login_time, save_otp, Login_Form, Login_Success, deviceFCMToken, surevyflag, childSelectAtom, shiftCenterFormState, pass, allowedService, cctvIntial } from '../../atomstore/store';
import { useNavigate } from 'react-router-dom';
import { clearSavedTokenCache, logoutParentUser } from '../../queryfunctions/queryfunctions';
import { setUserState } from '../../atomstore/serviceAtoms';
import { useRequestProcessor } from '../../requestProcessor';
import { useQueryClient } from 'react-query';
import { Capacitor, registerPlugin } from '@capacitor/core';

const ClearWebData = registerPlugin('ClearWebData');

const More = () => {
    const navigate = useNavigate()
    const { useMutationWrapper } = useRequestProcessor();
    const queryClient = useQueryClient();
    const [, setActiveTab] = useAtom(tab)
    const [, setAuthToken] = useAtom(auth_token);
    const [, setSurveyId] = useAtom(survey_id);
    const [, setChildList] = useAtom(childList);
    const [, setActiveChildDetail] = useAtom(activeChildDetail);
    const [, setParentProfile] = useAtom(parentProfile);
    const [, setLastReload] = useAtom(last_reload);
    const [, setLastLoginTime] = useAtom(last_login_time);
    const [, setSaveOtp] = useAtom(save_otp);
    const [, setLoginForm] = useAtom(Login_Form);
    const [, setLoginSuccess] = useAtom(Login_Success);
    const [, setDeviceFCMToken] = useAtom(deviceFCMToken);
    const [, setSurevyflag] = useAtom(surevyflag);
    const [, setChildSelectAtom] = useAtom(childSelectAtom);
    const [, setShiftCenterFormState] = useAtom(shiftCenterFormState);
    const [, setPass] = useAtom(pass);
    const [, setAllowedService] = useAtom(allowedService);
    const [, setCctvIntial] = useAtom(cctvIntial);
    const [modal, setModal] = useAtom(alertModal)
    const [constants] = useAtom(appConstants);

    const showReferFriendsInMore = (() => {
        const v = constants?.showReferFriendsInMore;
        if (v === true) return true;
        if (typeof v === 'string' && v.toLowerCase() === 'true') return true;
        return false;
    })();

    // Client-side cleanup function
    const performClientSideCleanup = () => {
        // Clear auth state
        localStorage.removeItem('auth_token');
        localStorage.removeItem('session_id');
        setAuthToken('');

        // Clear user-specific data from localStorage
        localStorage.removeItem('last_reload_time');
        localStorage.removeItem('childList');
        localStorage.removeItem('activeChildDetail');
        localStorage.removeItem('parentProfile');
        localStorage.removeItem('last_reload');
        localStorage.removeItem('last_login_time');
        localStorage.removeItem('survey_id');
        localStorage.removeItem('save_otp');
        localStorage.removeItem('Login');
        localStorage.removeItem('Login_Success');
        localStorage.removeItem('deviceFCMToken');
        localStorage.removeItem('survey');
        localStorage.removeItem('childSelect');
        localStorage.removeItem('shiftCenterFormState');
        localStorage.removeItem('passingChild');
        localStorage.removeItem('allowedServices');
        localStorage.removeItem('cctvIntial');
        localStorage.removeItem('userstate');

        // Reset atom states
        setSurveyId(-1);
        setChildList([]);
        setActiveChildDetail(null);
        setParentProfile(null);
        setLastReload(null);
        setLastLoginTime(null);
        setSaveOtp(null);
        setUserState(null);
        setLoginForm({
            email: "",
            isLogin: false,
            error: "",
            password: "",
            phone: "",
        });
        setLoginSuccess(false);
        setDeviceFCMToken(null);
        setSurevyflag(false);
        setChildSelectAtom(false);
        setShiftCenterFormState({
            attritionDate: null,
            joiningDate: null,
            selectedCenter: null,
            selectedProgram: null,
            selectedChild: 0,
        });
        setPass(null);
        setAllowedService(null);
        setCctvIntial({
            centerCctvDown: false,
            cctvUrl: ""
        });

        clearSavedTokenCache();

        setModal({
            type: "",
            isConfirm: false,
            onOkPressed: () => { },
            onCancelPressed: () => { },
            message: "",
            state: false,
            navigateTo: null
        });

        // Flush all cached API responses containing sensitive data
        queryClient.clear();

        // Clear sessionStorage
        sessionStorage.clear();

        setActiveTab('home');

        // Clear native WebView cache, cookies, and web storage on Android
        if (Capacitor.isNativePlatform()) {
            try {
                ClearWebData.clear();
            } catch (e) {
                console.warn("Failed to clear native web data:", e);
            }
        }

        // Force full page reload to flush the JS heap of residual sensitive data
        window.location.href = '/login';
    };

    // Logout mutation using useMutationWrapper
    const logoutMutation = useMutationWrapper(
        ["logoutParentUser"],
        () => logoutParentUser(),
        {
            onSuccess: (data) => {
                console.log("[logoutMutation] DB logout successful:", data);
                // Perform client-side cleanup after successful API call
                performClientSideCleanup();
            },
            onError: (error) => {
                console.warn("[logoutMutation] DB logout failed, but proceeding with client-side cleanup:", error);
                // Continue with client-side cleanup even if API call fails
                performClientSideCleanup();
            }
        }
    );

    const handleLogout = () => {
        // Trigger the logout mutation
        logoutMutation.mutate();
    };


    const moreOptions = [
        { icon: User, title: 'My Profile', desc: 'View and edit your account', screen: 'profile', gradient: 'from-orange-400 to-pink-500' },
        { icon: MessageCircle, title: 'Chat with Center', desc: 'Message your teacher directly', screen: 'chat', gradient: 'from-cyan-400 to-blue-500' },
        { icon: School, title: 'My School', desc: 'Center details & staff info', screen: 'my-school', gradient: 'from-purple-400 to-indigo-500' },
        { icon: PencilRuler, title: 'Growth Tracker', desc: 'Track developmental milestones', screen: 'growth', gradient: 'from-green-400 to-emerald-500' },
        { icon: Camera, title: 'Photo Gallery', desc: 'View all photos', screen: 'gallery', gradient: 'from-pink-400 to-rose-500' },
        { icon: Video, title: 'Parent Orientation Videos', desc: 'Educational resources', screen: 'videos', gradient: 'from-indigo-400 to-purple-500' },
        { icon: Newspaper, title: 'Newsroom', desc: 'Get the latest news info', screen: 'newsroom', gradient: 'from-green-400 to-emerald-500' },
        { icon: Activity, title: 'Outdoor Activity', desc: 'View activity request and status', screen: 'outdoor-activity', gradient: 'from-cyan-400 to-blue-500' },
        { icon: Gift, title: 'Refer Friends', desc: 'Share referral code & earn rewards', screen: 'referral', gradient: 'from-amber-400 to-orange-500' }
    ];

    const visibleMoreOptions = moreOptions.filter(
        (item) => item.screen !== 'referral' || showReferFriendsInMore
    );

    return (
        <div className="p-4 space-y-3">
            <h2 className="text-xl font-bold text-gray-800 mb-4">More</h2>

            {visibleMoreOptions.map((item, idx) => (
                <button
                    key={idx}
                    onClick={() => navigate('/' + item.screen)}
                    className="w-full bg-white rounded-2xl p-4 shadow-sm border border-gray-100 active:scale-95 transition"
                >
                    <div className="flex items-center space-x-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${item.gradient}`}>
                            <item.icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                            <p className="font-semibold text-gray-800">{item.title}</p>
                            <p className="text-sm text-gray-500">{item.desc}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                </button>
            ))}

            <button
                onClick={() => setModal({
                    ...modal,
                    state: true,
                    type: "Success",
                    isConfirm: true,
                    message: "Are you sure you want to logout ?",
                    onOkPressed: async () => { handleLogout() },
                    onCancelPressed: () => { },
                })}
                className="w-full bg-white rounded-2xl p-4 shadow-sm border border-gray-100 active:scale-95 transition"
            >
                <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-red-400 to-rose-500">
                        <LogOut className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                        <p className="font-semibold text-gray-800">Logout</p>
                        <p className="text-sm text-gray-500">Sign out of your account</p>
                    </div>
                </div>
            </button>
        </div>
    );
};

export default More;